import React, { useState } from 'react';
import { Mail, MapPin, Clock, Send, CheckCircle, Loader2 } from 'lucide-react';
import { API } from '../lib/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Submit as a lead with contact source
      const response = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          source: 'contact_page',
          interests: ['contact_inquiry']
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'info@ninjathlete.com',
      href: 'mailto:info@ninjathlete.com'
    },
    {
      icon: MapPin,
      label: 'Location',
      value: '3333 Harry Hines Blvd, Dallas, TX 75201',
      href: null
    },
    {
      icon: Clock,
      label: 'Response Time',
      value: 'Within 24-48 hours',
      href: null
    }
  ];

  return (
    <main className="pt-20 min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="container-ninja section-spacing">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <p className="text-ninja-label text-[#00F0FF] mb-4">Get in Touch</p>
          <h1 className="text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Contact <span className="text-gradient-ninja">NinjAthlete</span>
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed max-w-xl mx-auto">
            Have questions about biohacking, meditation protocols, or HYROX training? 
            Want to collaborate or share feedback? We'd love to hear from you.
          </p>
        </div>

        {/* Contact Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          
          {/* Contact Info Cards */}
          <div className="space-y-6">
            <h2 className="text-white text-2xl mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Reach Out
            </h2>
            
            <div className="space-y-4">
              {contactInfo.map((item) => (
                <div 
                  key={item.label}
                  className="flex items-center gap-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-zinc-700 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#E63946]/10 border border-[#E63946]/30 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-[#E63946]" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">{item.label}</p>
                    {item.href ? (
                      <a 
                        href={item.href}
                        className="text-white hover:text-[#00F0FF] transition-colors"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-white">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social/Additional Info */}
            <div className="p-6 bg-gradient-to-br from-zinc-900/80 to-zinc-900/40 border border-zinc-800 rounded-xl mt-8">
              <h3 className="text-white font-semibold mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Before You Write
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                For quick answers, try our AI-powered NinjaBot! Click the chat icon 
                in the bottom right corner for instant help with biohacking protocols, 
                meditation techniques, or training advice.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-zinc-900/30 border border-zinc-800 rounded-2xl p-6 sm:p-8">
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-green-600/20 flex items-center justify-center mb-6">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-white text-xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  Message Sent!
                </h3>
                <p className="text-zinc-400 mb-6">
                  Thank you for reaching out. We'll get back to you within 24-48 hours.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="btn-stealth"
                  data-testid="send-another-btn"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <h2 className="text-white text-xl mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  Send a Message
                </h2>
                
                {/* Name Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm text-zinc-400 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors"
                      placeholder="John"
                      data-testid="contact-firstname"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm text-zinc-400 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors"
                      placeholder="Ninja"
                      data-testid="contact-lastname"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm text-zinc-400 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors"
                    placeholder="john@example.com"
                    data-testid="contact-email"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm text-zinc-400 mb-2">
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#E63946] focus:outline-none transition-colors appearance-none"
                    data-testid="contact-subject"
                  >
                    <option value="" className="text-zinc-500">Select a topic...</option>
                    <option value="general">General Inquiry</option>
                    <option value="biohacking">Biohacking Questions</option>
                    <option value="hyrox">HYROX Training</option>
                    <option value="meditation">Meditation & Breathwork</option>
                    <option value="products">Products & Orders</option>
                    <option value="collaboration">Collaboration / Partnership</option>
                    <option value="feedback">Feedback / Suggestions</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm text-zinc-400 mb-2">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors resize-none"
                    placeholder="Tell us what's on your mind..."
                    data-testid="contact-message"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <p className="text-red-400 text-sm" data-testid="contact-error">
                    {error}
                  </p>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-ninja flex items-center justify-center gap-2 py-4"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
